/**
 * Render a mail template (by templateType) to { subject, body } HTML, off
 * the screen — no popup. This is the same pipeline EmailView runs inline
 * (fetch the htmltemplates catalog → build subject/body via the HtmlTemplate
 * model → expand {template:}/{phrase:}/{supplier:} placeholders), factored
 * out so a host that doesn't use the EmailView popup (e.g. the Sales Inbox
 * reply composer) can load the rendered template into its own editor.
 *
 * Kept deliberately parallel to EmailView.fetchTemplate / replacePlaceholders
 * (modules/components/email/view.js); if the rendering rules change there,
 * mirror them here.
 */
define('mailTemplateRenderer',[
  'app',
  'module',
  'jquery',
  'underscore',
  'settings',
  'entities/caccounts/htmltemplates'
], function (App, Module, $, _, Settings) {
  'use strict';

  // GET the account's template catalog (same call EmailView.fetchTemplates
  // makes). Resolves [] on failure so callers can degrade gracefully.
  function fetchTemplates() {
    var defer = $.Deferred();
    var params = {
      pageSize: 100, first: 0, _search: false,
      nd: new Date().getTime(), rows: 100, page: 1, sidx: '', sord: 'asc'
    };
    $.ajax({
      type: 'GET',
      url: Settings.url('compuzz2', 'v2/htmltemplates/advancedSearch', params),
      success: function (templates) { defer.resolve(templates || []); },
      error: function () { defer.resolve([]); }
    });
    return defer.promise();
  }

  // Mirror of EmailView.replacePlaceholders: expand nested {template:TYPE},
  // {phrase:CODE} and {supplier:attr} tokens left in a built template body.
  function expandPlaceholders(content, templates, templateData, locale) {
    if (!content) { return ''; }

    var regex = /\{template:([^\}]+)\}/g;
    var matches;
    while ((matches = regex.exec(content)) !== null) {
      try {
        for (var i = 0; i < templates.length; i++) {
          if (templates[i].templateType === matches[1]) {
            var model = App.request('htmltemplates:model', templates[i]);
            var nested = model.build(templateData, null, locale);
            content = content.replace('{template:' + matches[1] + '}', nested);
          }
        }
      } catch (e) { /* leave the token in place on render error */ }
    }

    content = content.replace(/\{phrase:([^\}]+)\}/g, function (match, phraseCode) {
      try {
        var phrase = templateData.phrases ?
          _.findWhere(templateData.phrases, { code: phraseCode }) : null;
        return phrase ? phrase.text : '';
      } catch (e) { return ''; }
    });

    if (templateData.supplier) {
      regex = /\{supplier:([^\}]+)\}/g;
      while ((matches = regex.exec(content)) !== null) {
        try {
          var attr = (matches && matches.length > 1) ? matches[1] : null;
          var value = (attr && templateData.supplier[attr]) ? templateData.supplier[attr] : null;
          if (value) { content = content.replace('{supplier:' + attr + '}', value); }
        } catch (e) { /* ignore a single bad token */ }
      }
    }

    return content;
  }

  // Normalise the `orders` option (EmailView passes a Backbone collection;
  // callers may pass an array of JSON) to a plain array of order JSON.
  function ordersToJSON(orders) {
    if (!orders) { return null; }
    if (typeof orders.toJSON === 'function') { return orders.toJSON(); }
    return _.isArray(orders) ? orders : null;
  }

  Module.exports = {
    /**
     * options mirrors the object EmailView.prototype.open consumes:
     *   { templateType, templateLocale, fetchTemplateData(type)->promise,
     *     orders }
     * Resolves { subject, body } (body is final HTML with placeholders
     * expanded); rejects when the template type isn't in the catalog or the
     * body fails to build.
     */
    render: function (options) {
      var defer = $.Deferred();
      options = options || {};

      fetchTemplates().done(function (templates) {
        var meta = _.findWhere(templates, { templateType: options.templateType });
        if (!meta) { defer.reject('template_not_found'); return; }

        var model = App.request('htmltemplates:model', meta);
        var dataPromise = options.fetchTemplateData ?
          options.fetchTemplateData(options.templateType) :
          $.Deferred().resolve({}).promise();

        $.when(dataPromise).done(function (data) {
          data = data || {};
          // Same defaults EmailView.fetchTemplate applies before build().
          if (!data.locale) { data.locale = options.templateLocale; }
          if (!data.user) { data.user = Settings.get('currentUser'); }
          var ordersJSON = ordersToJSON(options.orders);
          if (!data.orders && ordersJSON) { data.orders = ordersJSON; }
          if (!data.pois && ordersJSON) {
            data.pois = _.map(ordersJSON, function (o) { return o.purchaseOrderId; });
          }

          var subject = null, body = null;
          try { subject = model.buildSubject(data, null, options.templateLocale); }
          catch (e) { /* subject is optional for a reply */ }
          try { body = model.build(data, null, options.templateLocale); }
          catch (e) { defer.reject(e); return; }

          defer.resolve({
            subject: subject,
            body: expandPlaceholders(body || '', templates, data, options.templateLocale)
          });
        }).fail(function () { defer.reject('template_data_failed'); });
      });

      return defer.promise();
    }
  };
});

